<template>
    <div class="mx-2 mx-lg-5">
        <div class="mb-1 mt-3">
            <div class="d-flex align-items-center gap-3">
                <button
                    v-if="$store.getters.hasGatehousePerm('add_repeatingevent')"
                    class="btn btn-sm btn-primary px-3 py-2 d-flex align-items-center"
                    @click="addRepeatingEvent"
                >
                    <Icon icon="Plus"></Icon>
                    <span class="ms-2 fs-6 fw-bold">{{ $t("repeatingEvents.addRepeatingEvent") }}</span>
                </button>

                <button
                    v-if="$store.getters.hasGatehousePerm('add_vehicleentry')"
                    class="btn btn-sm btn-primary px-3 py-2 d-flex align-items-center"
                    @click="copyRecord"
                >
                    <Icon icon="Copy"></Icon>
                    <span class="ms-2 fs-6 fw-bold">{{ $t("utils.copy") }}</span>
                </button>

                <button
                    v-if="canEdit"
                    class="btn btn-primary btn-sm px-3 py-2 d-flex flex-row justify-content-center align-items-center"
                    @click="goToEdit"
                >
                    <Icon icon="Edit"></Icon>
                    <span class="ms-2 fs-6 fw-bold">{{ $t("utils.editDetails") }}</span>
                </button>

                <button
                    v-if="$store.getters.hasGatehousePerm('anonymize_guestbook') && guestbook.id && !guestbook.anonymized_at"
                    class="btn btn-outline-danger btn-sm px-3 py-2 ms-auto fw-bold"
                    @click="anonymize"
                >
                    {{ $t("guestbook.gdpr.button") }}
                </button>
            </div>
        </div>
        <div class="card mt-3">
            <div class="card-header">
                <h3>{{ $t("headers.guestbookDetail") }}</h3>
            </div>
            <div class="card-body">
                <div v-if="guestbook.anonymized_at" class="alert alert-secondary">
                    {{ $t("guestbook.gdpr.erased", { date: $d(new Date(guestbook.anonymized_at), 'short') }) }}
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <dl class="row mb-0 align-items-center">
                            <dt class="col-sm-4">{{ $t("guestbook.firstName") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.first_name }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.lastName") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.last_name }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.phone") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.phone || "—" }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.company") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.company }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.cardNumber") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.personal_card_number }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.cardType") }}</dt>
                            <dd class="col-sm-8">
                                {{ guestbook.personal_card_type ? $t("documentType." +
                                guestbook.personal_card_type) : "-" }}
                            </dd>

                            <dt class="col-sm-4">{{ $t("guestbook.isTrainedShort") }}</dt>
                            <dd class="col-sm-8">
                                <span v-if="guestbook.is_trained">{{ $t("utils.yes") }}</span>
                                <span v-else>{{ $t("utils.no") }}</span>
                            </dd>

                            <template v-if="guestbook.bozp_confirmation">
                                <dt class="col-sm-4">{{ $t("bozp.confirmedShort") }}</dt>
                                <dd class="col-sm-8">
                                    {{ $t("bozp.confirmedDetail", {
                                        version: guestbook.bozp_confirmation.version,
                                        date: $d(new Date(guestbook.bozp_confirmation.confirmed_at), 'short'),
                                    }) }}
                                    <button type="button" class="btn btn-sm btn-outline-primary ms-2"
                                        @click="viewConfirmedBozp">
                                        <Icon icon="Eye" class="me-1"></Icon>
                                        {{ $t("utils.view") }}
                                    </button>
                                </dd>
                            </template>


                            <dt class="col-sm-4">
                                <span v-if="guestbook.actual_from">
                                    {{ $t("guestbook.from") }}
                                </span>
                                <span v-else>{{ $t("guestbook.expectedFrom") }}</span>
                            </dt>
                            <dd class="col-sm-8">
                                <span v-if="guestbook.actual_from">
                                    {{ $d(new Date(guestbook.actual_from), 'short') }}
                                </span>
                                <span v-else-if="guestbook.expected_from">
                                    {{ $d(new Date(guestbook.expected_from), 'short') }}
                                </span>
                                <span v-else>—</span>
                            </dd>

                            <dt class="col-sm-4">
                                <span v-if="guestbook.actual_to">
                                    {{ $t("guestbook.to") }}
                                </span>
                                <span v-else>{{ $t("guestbook.expectedTo") }}</span>
                            </dt>
                            <dd class="col-sm-8">
                                <span v-if="guestbook.actual_to">
                                    {{ $d(new Date(guestbook.actual_to), 'short') }}
                                </span>
                                <span v-else-if="guestbook.expected_to">
                                    {{ $d(new Date(guestbook.expected_to), 'short') }}
                                </span>
                                <span v-else>—</span>
                            </dd>
                        </dl>
                    </div>

                    <div class="col-md-6">
                        <dl class="row mb-0 align-items-center">
                            <dt class="col-sm-4">{{ $t("status.status") }}</dt>
                            <dd class="col-sm-8">
                                <div
                                    class="d-flex flex-column flex-lg-row justify-content-between align-items-center w-75">
                                    <div class="d-flex align-item-center mb-2 mb-md-2">
                                        <Icon v-if="guestbook.status_icon" :icon="guestbook.status_icon" class="status_icon me-1" />
                                        <span>{{ guestbook.status_name ? $t("status.guestbook." + guestbook.status_name)
                                            : "-" }}</span>
                                    </div>

                                    <button
                                        v-if="$store.getters.hasGatehousePerm('change_status_guestbook') && guestbook.status_name !== 'left'"
                                        class="btn btn-primary fw-bold p-2 ms-5 w-100 d-flex align-items-center justify-content-center"
                                        @click="openStatusModal"
                                    >
                                        <Icon icon="Edit" class="me-1"></Icon>
                                        <span>{{ $t("utils.change") }}</span>
                                    </button>
                                </div>
                            </dd>


                            <dt class="col-sm-4 mt-3">{{ $t("guestbook.signatureArrival") }}</dt>
                            <dd class="col-sm-8 mt-3">
                                <div v-if="!signatureImages.arrival">
                                    <button v-if="$store.getters.hasGatehousePerm('add_signature') && !guestbook.anonymized_at" type="button"
                                        class="btn btn-primary fw-bold p-2 me-3 w-75" @click="startArrivalSignature">
                                        <Icon icon="Writing" class="me-1"></Icon>
                                        {{ $t("utils.add") }}
                                    </button>
                                    <div v-else class="text-muted">
                                        {{ $t("guestbook.noSignature") }}
                                    </div>
                                </div>
                                <div v-else>
                                    <button type="button" class="btn btn-primary fw-bold p-2 me-3 w-75"
                                        data-bs-toggle="modal" data-bs-target="#viewModal"
                                        @click="openViewModal('arrival')">
                                        <Icon icon="Eye" class="me-1"></Icon>
                                        {{ $t("utils.view") }}
                                    </button>
                                </div>
                            </dd>

                            <dt class="col-sm-4 mt-3">{{ $t("guestbook.signatureDeparture") }}</dt>
                            <dd class="col-sm-8 mt-3">
                                <div v-if="!signatureImages.departure">
                                    <button v-if="$store.getters.hasGatehousePerm('add_signature') && !guestbook.anonymized_at" type="button"
                                        class="btn btn-primary fw-bold p-2 me-3 w-75" data-bs-toggle="modal"
                                        data-bs-target="#signatureModal" @click="openSignatureModal('departure')">
                                        <Icon icon="Writing" class="me-1"></Icon>
                                        {{ $t("utils.add") }}
                                    </button>
                                    <div v-else class="text-muted">
                                        {{ $t("guestbook.noSignature") }}
                                    </div>
                                </div>
                                <div v-else>
                                    <button type="button" class="btn btn-primary fw-bold p-2 me-3 w-75"
                                        data-bs-toggle="modal" data-bs-target="#viewModal"
                                        @click="openViewModal('departure')">
                                        <Icon icon="Eye" class="me-1"></Icon>
                                        {{ $t("utils.view") }}
                                    </button>
                                </div>
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal for adding signatures -->
    <div class="modal fade" id="signatureModal" tabindex="-1" aria-hidden="true" ref="signatureModal">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content modal_wrapper">
                <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-auto">
                    <canvas ref="canvas" class="canvas" width="400" height="200"></canvas>
                </div>
                <div class="modal-footer border-top-0 mb-2">
                    <div class="d-flex justify-content-center gap-4 w-100">
                        <button type="button" class="btn btn-danger" @click="clearCanvas">{{ $t("utils.delete")
                            }}</button>
                        <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="saveCanvas">{{
                            $t("utils.save") }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal for signature view -->
    <div class="modal fade" id="viewModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content modal_wrapper">
                <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-auto">
                    <img v-if="signatureType && signatureImages[signatureType]" :src="getSignature" alt="signature" />
                </div>
            </div>
        </div>
    </div>

    <!-- Modal for BOZP: confirmation before the arrival signature, or view of the confirmed version -->
    <div class="modal fade" id="bozpModal" tabindex="-1" aria-hidden="true" ref="bozpModal">
        <div class="modal-dialog modal-fullscreen">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">
                        {{ $t("bozp.title") }}
                        <span v-if="bozp"> – {{ $t("bozp.version", { version: bozp.version }) }}</span>
                    </h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body overflow-hidden bozp_body">
                    <BozpViewer v-if="bozp && bozpShown" :key="bozp.id" :version="bozp"
                        @reached-end="bozpReachedEnd = true" />
                </div>
                <div v-if="bozpMode === 'confirm'" class="modal-footer flex-column align-items-stretch">
                    <div v-if="!bozpReachedEnd" class="alert alert-warning fw-bold fs-5 text-center mb-3" id="bozpScrollHint">
                        ↓ {{ $t("bozp.scrollToEnd") }} ↓
                    </div>
                    <div class="form-check confirm-check mb-3">
                        <input id="bozpConfirm" v-model="bozpConfirmed" class="form-check-input" type="checkbox"
                            :disabled="!bozpReachedEnd">
                        <label for="bozpConfirm" class="form-check-label fw-bold fs-5">
                            {{ $t("bozp.confirmLabel") }}
                        </label>
                    </div>
                    <button type="button" class="btn btn-primary btn-lg" :disabled="!bozpConfirmed"
                        @click="continueToSignature">
                        {{ $t("bozp.continue") }}
                    </button>
                </div>
            </div>
        </div>
    </div>
    <GuestbookStatusesModal :guestbook="guestbook" ref="statusModal" />
</template>

<script>
import Icon from '@/components/Icon.vue';
import GuestbookStatusesModal from '@/components/GuestbookStatusesModal.vue';
import BozpViewer from '@/components/BozpViewer.vue';
import { CONFIG } from "@/config.js"
import { Modal } from "bootstrap/dist/js/bootstrap.bundle.min.js";

export default {
    components: {
        Icon,
        GuestbookStatusesModal,
        BozpViewer,
    },
    data() {
        return {
            guestbook: {},
            modalTitle: null,
            signatureType: null,
            signatureImages: {
                arrival: null,
                departure: null
            },
            bozp: null,
            bozpMode: null,
            bozpShown: false,
            bozpReachedEnd: false,
            bozpConfirmed: false,
            bozpVersionId: null,
            signatureAfterBozp: false,
        }
    },
    mounted() {
        this.setupCanvas();
        // the viewer is mounted only once the modal is visible, so the PDF is rendered at the real width
        this.$refs.bozpModal.addEventListener("shown.bs.modal", () => { this.bozpShown = true; });
        this.$refs.bozpModal.addEventListener("hidden.bs.modal", this.onBozpHidden);
    },
    created() {
        this.getGuestbook();
    },
    methods: {
        anonymize() {
            const url = `/${this.$route.params.slug}/guestbooks/${this.guestbook.id}/anonymize`;
            const name = `${this.guestbook.first_name} ${this.guestbook.last_name}`.trim();
            // the count first: the erasure covers every visit of the person at this gatehouse and can't be undone
            this.$store.getters.api.get(url)
            .then(response => {
                if (!window.confirm(this.$t("guestbook.gdpr.confirm", { name, count: response.data.count }))) return;
                return this.$store.getters.api.post(url).then(result => {
                    alert(this.$t("guestbook.gdpr.done", { count: result.data.count }));
                    this.getGuestbook();
                });
            })
            .catch(error => {
                console.error("Error erasing personal data", error);
                alert(this.$t("error.undefined"));
            });
        },
        getGuestbook() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/guestbooks/${this.$route.params.id}`)
                .then(response => {
                    this.guestbook = response.data;

                    const arrivalImg = this.guestbook.signatures.find(sig => sig.is_arrival);
                    const departureImg = this.guestbook.signatures.find(sig => !sig.is_arrival);

                    this.signatureImages.arrival = arrivalImg?.image || null;
                    this.signatureImages.departure = departureImg?.image || null;
                })
                .catch(error => {
                    console.error("Error loading guestbook", error);
                });
        },
        goToEdit() {
            this.$router.push({
                name: "guestbookEditView",
                params: {
                    slug: this.$route.params.slug,
                    id: this.guestbook.id,
                }
            })
        },

        startArrivalSignature() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/bozp/current`)
                .then(response => {
                    if (response.data.bozp) {
                        this.openBozpModal(response.data.bozp, "confirm");
                    } else {
                        this.bozpVersionId = null;
                        this.showSignatureModal("arrival");
                    }
                })
                .catch(error => {
                    console.error("Error loading BOZP", error);
                    alert(this.$t("error.undefined"));
                });
        },
        viewConfirmedBozp() {
            const versionId = this.guestbook.bozp_confirmation.version_id;
            this.$store.getters.api.get(`/${this.$route.params.slug}/bozp/${versionId}`)
                .then(response => {
                    this.openBozpModal(response.data, "view");
                })
                .catch(error => {
                    console.error("Error loading BOZP", error);
                    alert(this.$t("error.undefined"));
                });
        },
        openBozpModal(bozp, mode) {
            this.bozp = bozp;
            this.bozpMode = mode;
            this.bozpReachedEnd = false;
            this.bozpConfirmed = false;
            Modal.getOrCreateInstance(this.$refs.bozpModal).show();
        },
        continueToSignature() {
            this.bozpVersionId = this.bozp.id;
            this.signatureAfterBozp = true;
            Modal.getOrCreateInstance(this.$refs.bozpModal).hide();
        },
        onBozpHidden() {
            this.bozpShown = false;
            // opened only after the BOZP modal is fully hidden, two Bootstrap modals must not overlap
            if (this.signatureAfterBozp) {
                this.signatureAfterBozp = false;
                this.showSignatureModal("arrival");
            }
        },
        showSignatureModal(type) {
            this.openSignatureModal(type);
            Modal.getOrCreateInstance(this.$refs.signatureModal).show();
        },
        openSignatureModal(type) {
            this.signatureType = type;
            this.modalTitle = this.$t("guestbook.signature." + type)
            this.clearCanvas();
        },
        openViewModal(type) {
            this.signatureType = type;
            this.modalTitle = this.$t("guestbook.signature." + type)
        },
        setupCanvas() {
            this.canvas = this.$refs.canvas
            this.ctx = this.canvas.getContext("2d");

            this.canvas.addEventListener("mousedown", this.startDrawing)
            this.canvas.addEventListener("mousemove", this.draw);
            this.canvas.addEventListener("mouseup", this.stopDrawing);
            this.canvas.addEventListener("mouseleave", this.stopDrawing);

            this.canvas.addEventListener("touchstart", this.startDrawing, { passive: false });
            this.canvas.addEventListener("touchmove", this.draw, { passive: false });
            this.canvas.addEventListener("touchend", this.stopDrawing);
            this.canvas.addEventListener("touchcancel", this.stopDrawing);
        },
        getPos(e) {
            const rect = this.canvas.getBoundingClientRect();
            const point = e.touches ? e.touches[0] : e;
            return {
                x: (point.clientX - rect.left) * (this.canvas.width / rect.width),
                y: (point.clientY - rect.top) * (this.canvas.height / rect.height),
            };
        },
        startDrawing(e) {
            e.preventDefault();
            const pos = this.getPos(e);
            this.drawing = true;
            this.ctx.beginPath();
            this.ctx.moveTo(pos.x, pos.y);
        },
        draw(e) {
            if (!this.drawing) return;
            e.preventDefault();
            const pos = this.getPos(e);
            this.ctx.lineTo(pos.x, pos.y);
            this.ctx.stroke();
        },
        stopDrawing() {
            this.drawing = false;
        },
        clearCanvas() {
            if (this.ctx) {
                this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            }
        },
        saveCanvas() {
            const isArrival = this.signatureType === "arrival";
            const guestbookId = this.guestbook.id;

            this.canvas.toBlob((blob) => {
                if (!blob) return;

                const filename = `${guestbookId}_${this.signatureType}.png`;
                const file = new File([blob], filename, { type: "image/png" });

                const formData = new FormData();
                formData.append("guestbook", guestbookId);
                formData.append("is_arrival", isArrival);
                formData.append("file", file);
                if (isArrival && this.bozpVersionId) {
                    formData.append("bozp_version", this.bozpVersionId);
                }

                this.$store.getters.api.post(`/${this.$route.params.slug}/signatures/add`, formData)
                    .then(() => {
                        this.bozpVersionId = null;
                        this.getGuestbook();
                    })
                    .catch(error => {
                        console.error("Error adding signature", error);
                        if (error.response?.data?.error_code === 561) {
                            alert(this.$t("error.561"));
                        }
                    })
            }, "image/png");
        },

        copyRecord() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/statuses`, {
                params: {model: "guestbook"}
            })
            .then(res => {
                const statuses = res.data;
                const reservedStatus = statuses.find(s => s.name === "reserved");

                this.$store.commit("setCopyRecord", {
                    guestbook: {
                        ...this.guestbook,
                        status: reservedStatus?.id
                    }
                });

                this.$router.push({
                    name: "guestbookAddView",
                    params: {
                        slug: this.$route.params.slug
                    }
                })
            })
            .catch(error => {
                console.error("Error copy record.", error);
            });
        },
        addRepeatingEvent() {
            this.$router.push({
                name: "AddRepeatingEvent_Guestbook",
                params: {
                    slug: this.$route.params.slug,
                    id: this.guestbook.id,
                }
            });
        },
        openStatusModal() {
            this.$nextTick(() => {
                this.$refs.statusModal.show();
            });
        }
    },
    computed: {
        getSignature() {
            const slug = this.$route.params.slug;
            const guestbookId = this.guestbook.id;
            const type = this.signatureType;
            const timestamp = new Date().getTime();
            var url = CONFIG.api.baseUrl;
            url += `/${slug}/guestbooks/${guestbookId}/signatures/file?type=${type}&_=${timestamp}`;
            return url
        },
        canEdit() {
            // an anonymized visit stays as it is, the server refuses to edit it
            if (this.guestbook.anonymized_at) return false;
            const status = this.guestbook.status_name;
            const canChange = this.$store.getters.hasGatehousePerm("change_guestbook");
            const canBeforeChange = this.$store.getters.hasGatehousePerm("before_change_guestbook");

            if (status === "left") {
                return canChange;
            }

            if (status === "reserved" || status === "arrived") {
                return canBeforeChange || canChange;
            }

            return false;
        },
    }
}
</script>

<style scoped>
.canvas {
    border: 1px solid #777070;
    background-color: white;
    touch-action: none;
}

.status_icon {
    width: 25px;
    height: 25px;
}

/* lets the viewer, not the modal body, scroll, so it can detect the end */
.bozp_body {
    min-height: 0;
}
</style>